import { Currency } from "@/components/Currency";
import { useModals } from "@/features/modals/hooks";
import { getAllOrdersIds } from "@/features/orders/utils";
import { useActiveProject } from "@/features/project/ActiveProject";
import {
  Icon,
  ListView,
  Loading,
  chevronRightSmallIcon,
  shoppingBagIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import {
  ListViewItemWithActions,
  ListViewItemWithActionsCheckbox,
  LocalizedDateAndTime,
  OneLineInfo,
  SectionContainer,
  translateOrderType,
} from "@repo/ui";
import { hasItems, isDefined, trackAnalytics } from "@repo/utils";

import { type SearchOrderResult } from "../../hooks/searchOrder";
import { type AddOrderState } from "./AddOrderReducer";

interface SelectOrderStepProps {
  state?: AddOrderState;
  selectOrders: (orderId: string[]) => void;
  customerOrders?: SearchOrderResult[];
  isLoading: boolean;
}

export const OrderResultList = ({
  state,
  selectOrders,
  customerOrders,
  isLoading,
}: SelectOrderStepProps) => {
  const { opportunity } = useActiveProject();
  const { pushModal } = useModals();
  const orderIds = getAllOrdersIds(opportunity);
  const orders = state?.orders ? state?.orders : (customerOrders ?? []);

  const onChange = (checked: boolean, item: SearchOrderResult) => {
    const existing = state?.orderNumbers?.find(
      (orderNumber) => orderNumber === item.orderNo
    );
    const newValue = existing
      ? state?.orderNumbers?.filter((f) => f !== item.orderNo)
      : [...(state?.orderNumbers ?? []), item.orderNo];
    selectOrders(newValue ?? []);
  };

  const list = orders
    ?.filter(isDefined)
    .sort(
      (a, b) =>
        new Date(b.orderCreationDate).getTime() -
        new Date(a.orderCreationDate).getTime()
    );
  return (
    <SectionContainer>
      {isLoading && <Loading text={t("prompt.loading")} />}
      {!isLoading && hasItems(list) && (
        <ListView id="order-result">
          {list.map((order) => (
            <ListViewItemWithActions
              key={order.orderNo}
              icon={shoppingBagIcon}
              title={`${translateOrderType(order.orderType)} ${order.orderNo}`}
              description={
                <div>
                  <OneLineInfo
                    info={[
                      order.orderStatus,
                      <Currency
                        key={order.orderNo}
                        value={order.orderAmount ?? 0}
                        currency={order.currencyCode}
                      />,
                    ]}
                  />
                  <OneLineInfo
                    small
                    info={[
                      <LocalizedDateAndTime
                        key="creationDate"
                        date={order.orderCreationDate}
                      />,
                      order.businessUnitCode,
                    ]}
                  />
                </div>
              }
              action={
                <>
                  <ListViewItemWithActionsCheckbox
                    subtle
                    label={`${translateOrderType(order.orderType)} ${order.orderNo}`}
                    id={`checkbox-${order.orderNo}`}
                    value={order.orderNo}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      onChange(e.target.checked, order);
                      trackAnalytics("change_order_number");
                    }}
                    checked={
                      orderIds.includes(order.orderNo) ||
                      state?.orderNumbers?.includes(order.orderNo)
                    }
                    defaultChecked={orderIds?.includes(order.orderNo)}
                    disabled={orderIds?.includes(order.orderNo)}
                  />
                  <Icon paths={chevronRightSmallIcon} />
                </>
              }
              onClick={() => {
                pushModal({ type: "ORDER", orderId: order.orderNo });
                trackAnalytics("open_order_modal");
              }}
            />
          ))}
        </ListView>
      )}
    </SectionContainer>
  );
};
